import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Plus, Pencil, X, AlertTriangle } from 'lucide-react';
import { colorForIndex } from '../../../lib/canEndianness/defaults';

const SignalList = ({ signals, selectedSignalId, overlappingSignalIds, outOfRangeSignalIds, onSelect, onEdit, onDelete, onAdd, addDisabled }) => {
  return (
    <Card className="technical-card" data-testid="can-endianness-signal-list">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Signals</CardTitle>
          <CardDescription>Click a signal to highlight it in the bit grid.</CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onAdd} disabled={addDisabled} data-testid="can-endianness-add-signal-btn">
          <Plus className="h-3.5 w-3.5" /> Add Signal
        </Button>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {signals.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center" data-testid="can-endianness-signal-list-empty">
            No signals yet. Add one, or select bits directly in the grid below.
          </p>
        )}
        {signals.map((s, i) => {
          const isSelected = s.id === selectedSignalId;
          const isOverlapping = overlappingSignalIds.has(s.id);
          const isOutOfRange = outOfRangeSignalIds.has(s.id);
          return (
            <div
              key={s.id}
              onClick={() => onSelect(isSelected ? null : s.id)}
              className={`flex items-center gap-2 rounded-md px-2.5 py-2 cursor-pointer transition-colors ${
                isSelected ? 'bg-autoxium-blue/10 ring-1 ring-autoxium-blue' : 'hover:bg-muted/50'
              }`}
              data-testid={`can-endianness-signal-row-${s.id}`}
            >
              <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: colorForIndex(i) }} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono text-sm font-bold truncate">{s.name || 'Unnamed'}</span>
                  <Badge variant="outline" className="font-mono text-[9px] uppercase shrink-0">
                    {s.byteOrder === 'motorola' ? 'Motorola' : 'Intel'}
                  </Badge>
                  <span className="font-mono text-[10px] text-muted-foreground shrink-0">{s.startBit}|{s.length}</span>
                </div>
                {(isOverlapping || isOutOfRange) && (
                  <div className="flex items-center gap-1 text-[10px] text-destructive mt-0.5">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    {isOverlapping ? 'Overlaps another signal' : `Exceeds current DLC`}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={(e) => { e.stopPropagation(); onEdit(s.id); }}
                aria-label="Edit signal"
                data-testid={`can-endianness-edit-${s.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                aria-label="Delete signal"
                data-testid={`can-endianness-delete-${s.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default SignalList;
